import { Button } from "@/components/ui/button"
import { FormField } from "pages/GenericOrder/ui-form/form-field"
import { ModalForm } from "pages/GenericOrder/ui-form/modal-form"
import { useCallback, useState, useMemo } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { z } from "zod"
import { toast } from "sonner"
import { useParams } from "react-router-dom"
import { Input } from "@/components/ui/input"
import { updateRateAndQuantity } from "../apis"
interface EditProductProps {
    children: React.ReactNode
    currency: string
    rate: number
    quantity: number
    totalValue?: number
    productId?: string,
    productName?: string
    oldPayable?: number
    disabled?: boolean
}

const schema = z.object({
    rate: z
        .number({
            invalid_type_error: `Rate is required`,
            required_error: `Rate is required`,
        })
        .min(0.01, 'Rate must be greater than 0')
        .refine(val => !isNaN(val), {
            message: `rate must be a number`,
        }),
    quantity: z
        .number({
            invalid_type_error: ` Quantity is required`,
            required_error: `Quantity is required`,
        })
        .min(1, 'Quantity must be at least 1')
        .refine(val => !isNaN(val), {
            message: `Quantity must be a number`,
        }),
})

export default function EditRateQuantityModal({ children, productName, oldPayable, currency, rate, quantity, productId, disabled }: EditProductProps) {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [inputValue, setInputValue] = useState<{
        rate: number;
        quantity: number;
    }>({
        rate,
        quantity,
    })
    const { id } = useParams()
    const queryClient = useQueryClient()

    // Memoize schema to prevent recreation on every render
    type FieldValues = z.infer<typeof schema>

    // Memoize mutation to prevent recreation
    const { mutate, isLoading } = useMutation({
        mutationFn: updateRateAndQuantity,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['orderDetails', id] })
            toast.success('Product updated successfully')
            setIsOpen(false)
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || 'Something went wrong')
        }
    })

    // Optimized useQuery for real-time calculation
    const { data: calculationResponse } = useQuery({
        queryKey: ['calculate', id, productId, inputValue],
        queryFn: async () => {
            const response = await updateRateAndQuantity({
                order_id: Number(id),
                uuid: productId!,
                inr_amount: inputValue.quantity * inputValue.rate,
                fx_amount: inputValue.quantity,
                rate: inputValue.rate,
                type: 'watch',
            })
            return response
        },
        enabled: isOpen && !!inputValue.rate && !!inputValue.quantity , // Only run when modal is open
        refetchOnWindowFocus: false,
        staleTime: 500, // Prevent too frequent calls
    })

    // Memoize handleSubmit to prevent recreation
    const handleSubmit = useCallback((data: FieldValues) => {
        mutate({
            order_id: Number(id),
            uuid: productId!,
            inr_amount: data[ 'rate'] * data['quantity'],
            fx_amount: data['quantity'],
            rate: data['rate'],
            type: 'edit',
        })     
    }, [mutate, id, productId, rate, quantity])

    // Memoize defaultValues to prevent recreation
    const defaultValues = useMemo(() => ({
        rate,
        quantity,
    }), [rate, quantity])

    // Reset inputValue when modal opens
    const handleModalOpen = useCallback(() => {
        if (disabled) return;
        setInputValue({
            rate,
            quantity,
        })
        setIsOpen(true)
    }, [rate, quantity, disabled])

    return (
        <>
            <Button variant='ghost' className="p-0 h-min" onClick={handleModalOpen} disabled={disabled}>
                {children}
            </Button>
            <ModalForm<FieldValues>
                title={`Edit ${productName}`}
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                onSubmit={data => handleSubmit(data)}
                submitButtonText="Confirm"
                schema={schema}
                defaultValues={defaultValues}
                isLoading={isLoading}
                className="max-w-[400px]"
            >
                {
                    (form) => {
                        return (
                            <div className="py-4 space-y-2">
                                <FormField
                                    label="Enter rate"
                                    form={form}
                                    name='rate'
                                    render={({ field }) => (                                            
                                            <Input
                                                type="number"
                                                {...field}
                                                value={field.value as number}
                                                onChange={e => {
                                                    const newValue = parseFloat(e.target.value)
                                                    field.onChange(newValue)
                                                    setInputValue(prev=>({...prev, rate: newValue}))
                                                }}
                                                className='m-0 h-12 max-w-[400px] border border-gray-border-gray-lite py-1.5 pr-[7px] rounded-lg'
                                                placeholder='Enter Rate'
                                            />
                                    )}
                                />
                                <FormField
                                    form={form}
                                    name='quantity'
                                    label="Enter FCY Amount"
                                    render={({ field }) => (
                                            <Input
                                                type="number"
                                                {...field}
                                                value={field.value as number}
                                                onChange={e => {
                                                    const newValue = parseInt(e.target.value, 10)
                                                    field.onChange(newValue)
                                                    setInputValue(prev=>({...prev, quantity: newValue}))
                                                }}
                                                className='m-0 h-12 max-w-[400px] border border-gray-border-gray-lite py-1.5 pr-[7px] rounded-lg'
                                                placeholder='Enter Quantity'
                                            />
                                    )}
                                />
                                {
                                    form.formState.isDirty &&
                                    <div className="space-y-3">
                                        <div className="bg-[#F9F9F9] rounded-lg p-2">
                                            <div className="flex items-center justify-between">
                                                <p className="">Old Total Payable</p>
                                                <p className="">{oldPayable}</p>
                                            </div>
                                        </div>
                                        <div className="p-2.5 rounded-b-xl bg-[#EDF3FF] border-t border-t-blue-700">
                                            <div className="flex items-center justify-between text-blue-700">
                                                <p className="font-bold">New Total Payable</p>
                                                <p className="">{calculationResponse?.data?.grand_total || 0}</p>
                                            </div>
                                        </div>
                                    </div>
                                }
                            </div>
                        )
                    }
                }
            </ModalForm>
        </>
    )
}